import { createFileRoute, Link, useSearch, useNavigate } from '@tanstack/react-router';
import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  ChevronRight,
  Save,
  Play,
  Plus,
  Trash2,
  GripVertical,
  Settings2,
  X,
  Cog,
  Globe,
  Zap,
  Code,
  FileJson,
} from 'lucide-react';
import {
  usePipelineDefinition,
  useStartPipeline,
  ActionButton,
  JsonViewer,
  type TaskConfig,
  type PipelineConfig,
  type HttpHandlerConfig,
} from '@/components/hermes';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Search params type
interface BuilderSearch {
  id?: string;
}

export const Route = createFileRoute('/hermes/builder')({
  component: BuilderPage,
  validateSearch: (search: Record<string, unknown>): BuilderSearch => ({
    id: typeof search.id === 'string' ? search.id : undefined,
  }),
});

// Handler types
type HandlerType = 'system' | 'edge-function' | 'http';

// System handlers - from _shared/hermes/system_handlers/
const SYSTEM_HANDLERS = [
  { id: 'system/sync-assets', label: 'Sync Assets' },
  { id: 'system/sync-courses', label: 'Sync Courses' },
  { id: 'system/sync-events', label: 'Sync Events' },
  { id: 'system/sync-experts', label: 'Sync Experts' },
  { id: 'system/sync-policies', label: 'Sync Policies' },
  { id: 'system/sync-companies', label: 'Sync Companies' },
];

// Edge functions - from config.toml [functions.*]
const EDGE_FUNCTIONS = [
  { id: 'pdf-processor', label: 'PDF Processor' },
  { id: 'image-resizer', label: 'Image Resizer' },
  { id: 'email-sender', label: 'Email Sender' },
  { id: 'webhook-handler', label: 'Webhook Handler' },
];

function getHandlerType(handler: string): HandlerType {
  if (handler.startsWith('system/')) return 'system';
  if (handler.startsWith('http://') || handler.startsWith('https://')) return 'http';
  return 'edge-function';
}

function getHandlerLabel(handler: string): string {
  const systemHandler = SYSTEM_HANDLERS.find(h => h.id === handler);
  if (systemHandler) return systemHandler.label;
  const edgeFunc = EDGE_FUNCTIONS.find(h => h.id === handler);
  if (edgeFunc) return edgeFunc.label;
  if (handler.startsWith('http')) return 'HTTP Webhook';
  return handler;
}

// Handler type icon
function HandlerTypeIcon({ type }: { type: HandlerType }) {
  switch (type) {
    case 'system':
      return <Cog className="w-3.5 h-3.5 text-blue-400" />;
    case 'edge-function':
      return <Zap className="w-3.5 h-3.5 text-amber-400" />;
    case 'http':
      return <Globe className="w-3.5 h-3.5 text-emerald-400" />;
  }
}

// Custom node component
function TaskNode({ data, selected }: { data: TaskNodeData; selected: boolean }) {
  const handlerType = getHandlerType(data.handler);
  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-[180px] ${
        selected ? 'border-primary' : 'border-border'
      }`}
    >
      <Handle type="target" position={Position.Top} className="!bg-primary !w-3 !h-3" />
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <HandlerTypeIcon type={handlerType} />
            <p className="font-medium text-foreground text-sm truncate">{data.label}</p>
          </div>
          <p className="text-xs text-muted-foreground truncate">{data.handlerLabel}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-primary !w-3 !h-3" />
    </div>
  );
}

interface TaskNodeData {
  label: string;
  handler: string;
  handlerLabel: string;
  timeout: number;
  maxRetries: number;
  httpConfig?: HttpHandlerConfig;
}

const nodeTypes: NodeTypes = {
  task: TaskNode,
};

// Dagre layout helper
function getLayoutedElements(nodes: Node[], edges: Edge[], direction = 'TB') {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 80, nodesep: 50 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 200, height: 60 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 100,
        y: nodeWithPosition.y - 30,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

function BuilderPage() {
  const { id: definitionId } = useSearch({ from: '/hermes/builder' });
  const { data: existingDefinition } = usePipelineDefinition(definitionId ?? '');
  const startPipeline = useStartPipeline();
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('1.0.0');

  // Selected task for editing
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // JSON preview drawer
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  // Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Load existing definition
  useEffect(() => {
    if (existingDefinition) {
      setName(existingDefinition.name);
      setDescription(existingDefinition.description ?? '');
      setVersion(existingDefinition.version);

      // Convert config to nodes/edges
      const config = existingDefinition.config as PipelineConfig;
      if (config?.tasks) {
        const newNodes: Node[] = config.tasks.map((task, index) => ({
          id: task.name,
          type: 'task',
          position: { x: 0, y: index * 100 },
          data: {
            label: task.name,
            handler: task.handler,
            handlerLabel: getHandlerLabel(task.handler),
            timeout: task.timeout,
            maxRetries: task.maxRetries,
          },
        }));

        const newEdges: Edge[] = [];
        config.tasks.forEach((task) => {
          task.dependsOn?.forEach((dep) => {
            newEdges.push({
              id: `${dep}-${task.name}`,
              source: dep,
              target: task.name,
              markerEnd: { type: MarkerType.ArrowClosed },
            });
          });
        });

        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(newNodes, newEdges);
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
      }
    }
  }, [existingDefinition, setNodes, setEdges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedTaskId(node.id);
  }, []);

  const addTask = () => {
    const taskId = `task-${nodes.length + 1}`;
    const defaultHandler = SYSTEM_HANDLERS[0].id;
    const newNode: Node = {
      id: taskId,
      type: 'task',
      position: { x: 250, y: nodes.length * 100 + 50 },
      data: {
        label: taskId,
        handler: defaultHandler,
        handlerLabel: getHandlerLabel(defaultHandler),
        timeout: 300,
        maxRetries: 3,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedTaskId(taskId);
  };

  const deleteTask = (taskId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== taskId));
    setEdges((eds) => eds.filter((e) => e.source !== taskId && e.target !== taskId));
    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
    }
  };

  const updateTask = (taskId: string, updates: Partial<TaskNodeData>) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === taskId) {
          return {
            ...node,
            id: updates.label ?? node.id,
            data: { ...node.data, ...updates },
          };
        }
        return node;
      })
    );

    // Update edges if task name changed
    if (updates.label && updates.label !== taskId) {
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          id: edge.id.replace(taskId, updates.label!),
          source: edge.source === taskId ? updates.label! : edge.source,
          target: edge.target === taskId ? updates.label! : edge.target,
        }))
      );
      setSelectedTaskId(updates.label);
    }
  };

  const autoLayout = () => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  };

  // Build config from nodes/edges
  const buildConfig = (): PipelineConfig => {
    const tasks: TaskConfig[] = nodes.map((node) => {
      const deps = edges.filter((e) => e.target === node.id).map((e) => e.source);
      const task: TaskConfig = {
        name: node.id,
        handler: node.data.handler as string,
        timeout: node.data.timeout as number,
        maxRetries: node.data.maxRetries as number,
        dependsOn: deps,
      };

      // Add httpConfig if present
      if (node.data.httpConfig) {
        task.httpConfig = node.data.httpConfig as HttpHandlerConfig;
      }

      return task;
    });
    return { tasks };
  };

  // Build full pipeline definition for preview
  const buildPipelineDefinition = () => {
    return {
      name,
      description: description || null,
      version,
      is_active: true,
      config: buildConfig(),
    };
  };

  const handleSave = async (andRun = false) => {
    if (!name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }
    if (nodes.length === 0) {
      toast.error('Add at least one task');
      return;
    }

    const config = buildConfig();

    try {
      if (definitionId && existingDefinition) {
        // Update existing
        const { error } = await supabase
          .from('pipeline_definitions')
          .update({
            name,
            description: description || null,
            version,
            config,
            updated_at: new Date().toISOString(),
          })
          .eq('id', definitionId);

        if (error) throw error;
        toast.success('Pipeline updated');
      } else {
        // Create new
        const { error } = await supabase.from('pipeline_definitions').insert({
          name,
          description: description || null,
          version,
          config,
          is_active: true,
        });

        if (error) throw error;
        toast.success('Pipeline created');
      }

      if (andRun) {
        try {
          await startPipeline.mutateAsync({ pipelineName: name });
          toast.success('Pipeline started');
          // Navigate to definitions list on success
          navigate({ to: '/hermes/definitions' });
        } catch (runError) {
          toast.error(runError instanceof Error ? runError.message : 'Failed to start pipeline');
          // Still navigate to definitions on error so user can see the issue
          navigate({ to: '/hermes/definitions' });
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedTaskId);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to="/hermes" className="hover:text-foreground">Hermes</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/hermes/definitions" className="hover:text-foreground">Definitions</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Builder</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Pipeline Builder</h1>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton onClick={async () => setShowJsonPreview(true)} size="sm" variant="secondary">
            <FileJson className="w-3.5 h-3.5" />
            View JSON
          </ActionButton>
          <ActionButton onClick={() => handleSave(false)} size="sm">
            <Save className="w-3.5 h-3.5" />
            Save
          </ActionButton>
          <ActionButton onClick={() => handleSave(true)} size="sm" variant="primary">
            <Play className="w-3.5 h-3.5" />
            Save & Run
          </ActionButton>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left sidebar - Details */}
        <div className="w-64 flex-shrink-0 space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h3 className="font-semibold text-foreground text-sm">Pipeline Details</h3>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="my-pipeline"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows={2}
                placeholder="Pipeline description..."
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Version</label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="1.0.0"
              />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground text-sm">Tasks</h3>
              <button
                onClick={addTask}
                className="p-1 rounded hover:bg-secondary transition-colors"
                title="Add task"
              >
                <Plus className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-1">
              {nodes.map((node) => (
                <div
                  key={node.id}
                  onClick={() => setSelectedTaskId(node.id)}
                  className={`flex items-center justify-between px-2 py-1.5 rounded text-sm cursor-pointer ${
                    selectedTaskId === node.id ? 'bg-primary/20 text-primary' : 'hover:bg-secondary'
                  }`}
                >
                  <span className="truncate">{node.id}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTask(node.id);
                    }}
                    className="p-0.5 rounded hover:bg-red-500/20"
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                </div>
              ))}
              {nodes.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No tasks yet
                </p>
              )}
            </div>
            <button
              onClick={autoLayout}
              className="w-full mt-3 px-2 py-1.5 text-xs rounded border border-border hover:bg-secondary transition-colors"
            >
              Auto Layout
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 rounded-lg border border-border bg-card overflow-hidden">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-background"
          >
            <Controls className="!bg-card !border-border !shadow-none" />
            <Background gap={20} size={1} color="hsl(var(--border))" />
          </ReactFlow>
        </div>

        {/* Right sidebar - Task config */}
        <div className="w-72 flex-shrink-0">
          {selectedNode ? (
            <TaskConfigPanel
              node={selectedNode}
              edges={edges}
              onUpdate={(updates) => updateTask(selectedNode.id, updates)}
              onClose={() => setSelectedTaskId(null)}
            />
          ) : (
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Settings2 className="w-4 h-4" />
                <span className="text-sm">Select a task to configure</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* JSON Preview Drawer */}
      {showJsonPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end" onClick={() => setShowJsonPreview(false)}>
          <div
            className="bg-card h-full w-[600px] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Pipeline Definition JSON</h2>
              </div>
              <button
                onClick={() => setShowJsonPreview(false)}
                className="p-2 rounded hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <JsonViewer data={buildPipelineDefinition()} />
            </div>
            <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-secondary/30">
              <p className="text-sm text-muted-foreground">
                This JSON will be saved to <code className="px-1.5 py-0.5 rounded bg-secondary text-xs">pipeline_definitions.config</code>
              </p>
              <ActionButton onClick={async () => setShowJsonPreview(false)} size="sm">
                Close
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface TaskConfigPanelProps {
  node: Node;
  edges: Edge[];
  onUpdate: (updates: Partial<TaskNodeData>) => void;
  onClose: () => void;
}

function TaskConfigPanel({ node, edges, onUpdate, onClose }: TaskConfigPanelProps) {
  const data = node.data as unknown as TaskNodeData;

  // Track handler type as explicit state to prevent re-classification while typing
  const [selectedType, setSelectedType] = useState<HandlerType>(() => getHandlerType(data.handler));
  const [httpUrl, setHttpUrl] = useState(() =>
    getHandlerType(data.handler) === 'http' ? data.handler : ''
  );

  // Get dependencies (incoming edges to this node)
  const dependencies = edges
    .filter((e) => e.target === node.id)
    .map((e) => e.source);

  // Reset state when switching to a different node
  useEffect(() => {
    const type = getHandlerType(data.handler);
    setSelectedType(type);
    setHttpUrl(type === 'http' ? data.handler : '');
  }, [node.id]); // Only reset when node changes, not when data changes

  const handleTypeChange = (newType: HandlerType) => {
    setSelectedType(newType);

    let newHandler: string;
    let newLabel: string;

    switch (newType) {
      case 'system':
        newHandler = SYSTEM_HANDLERS[0].id;
        newLabel = SYSTEM_HANDLERS[0].label;
        break;
      case 'edge-function':
        newHandler = EDGE_FUNCTIONS[0].id;
        newLabel = EDGE_FUNCTIONS[0].label;
        break;
      case 'http':
        newHandler = httpUrl || 'https://';
        newLabel = 'HTTP Webhook';
        break;
    }

    onUpdate({ handler: newHandler, handlerLabel: newLabel });
  };

  const handleHandlerSelect = (handler: string) => {
    onUpdate({
      handler,
      handlerLabel: getHandlerLabel(handler),
    });
  };

  const handleHttpUrlChange = (url: string) => {
    setHttpUrl(url);
    onUpdate({
      handler: url,
      handlerLabel: 'HTTP Webhook',
    });
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 bg-secondary/50 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-foreground text-sm">Task Configuration</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-secondary">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      <div className="p-4 space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Task Name</label>
          <input
            type="text"
            value={data.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Dependencies display */}
        {dependencies.length > 0 && (
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Depends On</label>
            <div className="flex flex-wrap gap-1">
              {dependencies.map((dep) => (
                <span
                  key={dep}
                  className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary"
                >
                  {dep}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Handler Type Selection */}
        <div>
          <label className="block text-xs text-muted-foreground mb-2">Handler Type</label>
          <div className="flex gap-1">
            <button
              onClick={() => handleTypeChange('system')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border transition-colors ${
                selectedType === 'system'
                  ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                  : 'border-border hover:bg-secondary'
              }`}
            >
              <Cog className="w-3 h-3" />
              System
            </button>
            <button
              onClick={() => handleTypeChange('edge-function')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border transition-colors ${
                selectedType === 'edge-function'
                  ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                  : 'border-border hover:bg-secondary'
              }`}
            >
              <Zap className="w-3 h-3" />
              Edge Fn
            </button>
            <button
              onClick={() => handleTypeChange('http')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border transition-colors ${
                selectedType === 'http'
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                  : 'border-border hover:bg-secondary'
              }`}
            >
              <Globe className="w-3 h-3" />
              HTTP
            </button>
          </div>
        </div>

        {/* Handler Selection based on type */}
        {selectedType === 'system' && (
          <div>
            <label className="block text-xs text-muted-foreground mb-1">System Handler</label>
            <select
              value={data.handler}
              onChange={(e) => handleHandlerSelect(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {SYSTEM_HANDLERS.map((h) => (
                <option key={h.id} value={h.id}>{h.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              From _shared/hermes/system_handlers/
            </p>
          </div>
        )}

        {selectedType === 'edge-function' && (
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Edge Function</label>
            <select
              value={data.handler}
              onChange={(e) => handleHandlerSelect(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {EDGE_FUNCTIONS.map((h) => (
                <option key={h.id} value={h.id}>{h.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Registered in config.toml
            </p>
          </div>
        )}

        {selectedType === 'http' && (
          <>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Webhook URL</label>
              <input
                type="url"
                value={httpUrl}
                onChange={(e) => handleHttpUrlChange(e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Full URL for HTTP/Webhook handler
              </p>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground mb-1">HTTP Method</label>
              <select
                value={data.httpConfig?.method || 'POST'}
                onChange={(e) => onUpdate({
                  httpConfig: {
                    ...data.httpConfig,
                    method: e.target.value as 'GET' | 'POST' | 'PUT' | 'DELETE',
                  }
                })}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground mb-1">Request Timeout (ms)</label>
              <input
                type="number"
                value={data.httpConfig?.timeout || 30000}
                onChange={(e) => onUpdate({
                  httpConfig: {
                    ...data.httpConfig,
                    timeout: parseInt(e.target.value) || 30000,
                  }
                })}
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.httpConfig?.passAuthHeader !== false}
                  onChange={(e) => onUpdate({
                    httpConfig: {
                      ...data.httpConfig,
                      passAuthHeader: e.target.checked,
                    }
                  })}
                  className="rounded border-border"
                />
                Pass X-User-Id header
              </label>
              <p className="text-xs text-muted-foreground mt-1 ml-5">
                Automatically includes user ID in request headers
              </p>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground mb-1">Custom Headers (JSON)</label>
              <textarea
                value={JSON.stringify(data.httpConfig?.headers || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const headers = JSON.parse(e.target.value);
                    onUpdate({
                      httpConfig: {
                        ...data.httpConfig,
                        headers,
                      }
                    });
                  } catch (err) {
                    // Invalid JSON - don't update
                  }
                }}
                placeholder='{\n  "Authorization": "Bearer token",\n  "X-Custom": "value"\n}'
                className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono resize-none"
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Additional headers as JSON object
              </p>
            </div>
          </>
        )}

        <div>
          <label className="block text-xs text-muted-foreground mb-1">Timeout (seconds)</label>
          <input
            type="number"
            value={data.timeout}
            onChange={(e) => onUpdate({ timeout: parseInt(e.target.value) || 300 })}
            className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-xs text-muted-foreground mb-1">Max Retries</label>
          <input
            type="number"
            value={data.maxRetries}
            onChange={(e) => onUpdate({ maxRetries: parseInt(e.target.value) || 3 })}
            className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Connect tasks by dragging from the bottom handle to the top handle of another task.
          </p>
        </div>
      </div>
    </div>
  );
}
